import React from "react";
import { Button } from "../ui/button";
import { ArrowRight, Sparkles, Layers, Zap } from "lucide-react";
import { siteInfo } from "../../data/mock";

const WhatsAppIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

export const HeroSection = ({ siteContent }) => {
  const whatsappNumber = siteContent?.whatsapp_number || siteInfo.whatsappNumber;
  const whatsappLink = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent("Hi Frostyware 3D! I'm interested in your 3D printing services.")}`;
  const headline = siteContent?.hero_headline || "Bring Your Ideas to Life with Frostyware 3D";
  const subheadline = siteContent?.hero_subheadline || "Premium 3D Printing, 3D Modeling, and Custom Creations in Chandigarh.";
  const stat1Value = siteContent?.stat1_value || "500+";
  const stat1Label = siteContent?.stat1_label || "Projects Done";
  const stat2Value = siteContent?.stat2_value || "100%";
  const stat2Label = siteContent?.stat2_label || "Customizable";
  return (
    <section className="relative min-h-screen flex items-center overflow-hidden bg-gradient-to-b from-sky-50/60 via-white to-white">
      {/* Decorative background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-sky-100/30 blur-3xl animate-float-slow" />
        <div className="absolute top-1/3 -left-20 w-[400px] h-[400px] rounded-full bg-cyan-50/50 blur-3xl animate-float-slower" />
        <div className="absolute bottom-20 right-1/4 w-[300px] h-[300px] rounded-full bg-sky-50/40 blur-3xl" />
        {/* Grid pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(14,165,233,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(14,165,233,0.03)_1px,transparent_1px)] bg-[size:60px_60px]" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 pt-28 pb-20">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left content */}
          <div className="space-y-8 animate-fade-in">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-sky-50 border border-sky-100">
              <Sparkles className="w-4 h-4 text-sky-500" />
              <span className="text-sm font-medium text-sky-700">
                Premium 3D Printing in Chandigarh
              </span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-[3.5rem] xl:text-6xl font-bold text-slate-900 leading-[1.1] tracking-tight">
              {headline.includes("Frostyware 3D") ? (
                <>
                  {headline.split("Frostyware 3D")[0]}
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-500 to-cyan-400">
                    Frostyware 3D
                  </span>
                  {headline.split("Frostyware 3D")[1]}
                </>
              ) : (
                headline
              )}
            </h1>

            <p className="text-lg text-slate-500 max-w-lg leading-relaxed">
              {subheadline} From concept to reality — we make it happen.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                onClick={() => window.open(whatsappLink, "_blank")}
                className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-full px-8 h-13 text-base font-semibold shadow-lg shadow-emerald-500/20 hover:shadow-emerald-500/35 transition-all duration-300 hover:-translate-y-0.5"
              >
                <WhatsAppIcon className="w-5 h-5 mr-2" />
                Order on WhatsApp
              </Button>
              <Button
                variant="outline"
                onClick={() =>
                  document
                    .getElementById("products")
                    ?.scrollIntoView({ behavior: "smooth" })
                }
                className="rounded-full px-8 h-13 text-base font-semibold border-slate-200 hover:border-sky-300 hover:bg-sky-50/50 transition-all duration-300"
              >
                View Products
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>

            {/* Stats */}
            <div className="flex gap-8 pt-4">
              <div>
                <div className="text-2xl font-bold text-slate-900">{stat1Value}</div>
                <div className="text-sm text-slate-400">{stat1Label}</div>
              </div>
              <div className="w-px bg-slate-200" />
              <div>
                <div className="text-2xl font-bold text-slate-900">{stat2Value}</div>
                <div className="text-sm text-slate-400">{stat2Label}</div>
              </div>
            </div>
          </div>

          {/* Right visual */}
          <div className="relative hidden lg:block animate-fade-in-delayed">
            <div className="relative">
              {/* Main image */}
              <div className="rounded-3xl overflow-hidden shadow-2xl shadow-sky-200/40 border border-sky-100/60">
                <img
                  src="https://images.unsplash.com/photo-1642969164999-979483e21601?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwxfHwzRCUyMHByaW50aW5nfGVufDB8fHx8MTc3MTU3OTc2OXww&ixlib=rb-4.1.0&q=85"
                  alt="3D Printing in action"
                  className="w-full h-[480px] object-cover"
                />
              </div>

              {/* Floating card - bottom left */}
              <div className="absolute -bottom-5 -left-5 bg-white/90 backdrop-blur-xl rounded-2xl p-4 shadow-xl shadow-slate-200/50 border border-sky-50 animate-float-slow">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl bg-sky-100 flex items-center justify-center">
                    <Layers className="w-5 h-5 text-sky-600" />
                  </div>
                  <div>
                    <div className="text-sm font-bold text-slate-900">
                      High Precision
                    </div>
                    <div className="text-xs text-slate-500">
                      0.1mm Layer Accuracy
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating card - top right */}
              <div className="absolute -top-4 -right-4 bg-white/90 backdrop-blur-xl rounded-2xl p-3.5 shadow-xl shadow-slate-200/50 border border-sky-50 animate-float-slower">
                <div className="flex items-center gap-2.5">
                  <div className="w-9 h-9 rounded-lg bg-cyan-100 flex items-center justify-center">
                    <Zap className="w-4 h-4 text-cyan-600" />
                  </div>
                  <div>
                    <div className="text-xs font-bold text-slate-900">
                      Fast Turnaround
                    </div>
                    <div className="text-[11px] text-slate-500">Same day prints</div>
                  </div>
                </div>
              </div>

              {/* Decorative shapes */}
              <div className="absolute top-1/2 -right-8 w-14 h-14 rounded-full bg-cyan-100/40 backdrop-blur-sm border border-cyan-200/20" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
